import { create } from "zustand";
import { persist } from "zustand/middleware";
import {
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
  type Unsubscribe,
} from "firebase/auth";
import {
  arrayRemove,
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  getDoc,
  onSnapshot,
  query,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { auth, db } from "@/lib/firebase";
import {
  AppUser,
  CartItem,
  Notification,
  Order,
  Product,
  Review,
  UserRole,
} from "@/lib/types";

// The data + auth layer for the app. Auth runs on Firebase Auth
// (email/password); users, products, orders, reviews, notifications and
// favourites live in Firestore, kept in sync here via onSnapshot listeners.
// Only the cart is local-only (zustand persisted to localStorage) — it's
// ephemeral pre-purchase state that doesn't need to be shared or durable.

interface AppState {
  _currentUser: AppUser | null;
  _authReady: boolean;
  users: AppUser[];
  products: Product[];
  orders: Order[];
  reviews: Review[];
  notifications: Notification[];
  cart: CartItem[];
  favourites: Record<string, string[]>; // userId -> productIds (only the signed-in user's entry is populated)

  currentUser: () => AppUser | null;

  signup: (
    email: string,
    password: string,
    displayName: string,
    role: UserRole
  ) => Promise<AppUser>;
  login: (email: string, password: string) => Promise<AppUser>;
  logout: () => Promise<void>;

  applyToSell: (userId: string) => Promise<void>;
  approveSeller: (userId: string) => Promise<void>;
  rejectSeller: (userId: string) => Promise<void>;

  addProduct: (
    product: Omit<Product, "id" | "status" | "createdAt">
  ) => Promise<Product>;
  approveProduct: (productId: string) => Promise<void>;
  rejectProduct: (productId: string) => Promise<void>;
  toggleRareFind: (productId: string) => Promise<void>;
  updateProduct: (
    productId: string,
    updates: Partial<
      Pick<
        Product,
        | "title"
        | "description"
        | "price"
        | "condition"
        | "category"
        | "images"
        | "status"
        | "isRareFind"
        | "isNew"
      >
    >
  ) => Promise<void>;
  deleteProduct: (productId: string) => Promise<void>;

  addToCart: (productId: string, quantity?: number) => void;
  removeFromCart: (productId: string) => void;
  clearCart: () => void;
  /** Creates a pending order + Stripe Checkout Session server-side, then
   * redirects the browser to Stripe's hosted checkout page. Returns false
   * (without redirecting) if the cart is empty or the request fails. */
  checkout: () => Promise<boolean>;

  /** Starts (or resumes) Stripe Connect Express onboarding for the current
   * seller and redirects to Stripe's hosted onboarding flow. */
  startSellerPayoutsOnboarding: () => Promise<void>;
  /** Re-checks the current seller's Stripe Connect status with Stripe
   * (also refreshes the cached flags on their user doc). */
  refreshSellerPayoutsStatus: () => Promise<{
    connected: boolean;
    chargesEnabled?: boolean;
    payoutsEnabled?: boolean;
    detailsSubmitted?: boolean;
  }>;

  toggleFavourite: (userId: string, productId: string) => Promise<void>;

  addReview: (review: Omit<Review, "id" | "createdAt">) => Promise<void>;
  notify: (userId: string, message: string) => Promise<void>;
  markNotificationsRead: (userId: string) => Promise<void>;
}

// A stable reference to hand back from selectors instead of `?? []`, which
// would otherwise allocate a new array every call and make useSyncExternalStore
// (which zustand's hook is built on) re-render in an infinite loop.
export const EMPTY_ARRAY: readonly string[] = [];

function requireDb() {
  if (!db || !auth) {
    throw new Error(
      "Firebase isn't configured. Add Firebase env vars (see .env.local.example) to use live data."
    );
  }
  return { db, auth };
}

/** Calls one of this app's own `/api/*` Worker routes, attaching the
 * current user's Firebase ID token so the server can verify who's asking. */
async function callApi<T>(path: string, options: RequestInit = {}): Promise<T> {
  const { auth } = requireDb();
  const user = auth.currentUser;
  if (!user) throw new Error("You need to be signed in to do that.");
  const idToken = await user.getIdToken();

  const res = await fetch(path, {
    ...options,
    headers: {
      ...(options.body ? { "Content-Type": "application/json" } : {}),
      Authorization: `Bearer ${idToken}`,
      ...options.headers,
    },
  });

  const data = (await res.json().catch(() => ({}))) as T & { error?: string };
  if (!res.ok) {
    throw new Error(data.error ?? `Request to ${path} failed (${res.status})`);
  }
  return data;
}

export const useAppStore = create<AppState>()(
  persist(
    (set, get) => ({
      _currentUser: null,
      _authReady: false,
      users: [],
      products: [],
      orders: [],
      reviews: [],
      notifications: [],
      cart: [],
      favourites: {},

      currentUser: () => get()._currentUser,

      signup: async (email, password, displayName, role) => {
        const { db, auth } = requireDb();
        const credential = await createUserWithEmailAndPassword(auth, email, password);
        const user: AppUser = {
          uid: credential.user.uid,
          email,
          displayName,
          role,
          sellerStatus: role === "seller" ? "pending" : "none",
          createdAt: Date.now(),
        };
        await setDoc(doc(db, "users", user.uid), user);
        return user;
      },

      login: async (email, password) => {
        const { db, auth } = requireDb();
        const credential = await signInWithEmailAndPassword(auth, email, password);
        const snap = await getDoc(doc(db, "users", credential.user.uid));
        if (!snap.exists()) {
          throw new Error("No profile found for this account.");
        }
        return snap.data() as AppUser;
      },

      logout: async () => {
        const { auth } = requireDb();
        await signOut(auth);
      },

      applyToSell: async (userId) => {
        const { db } = requireDb();
        await updateDoc(doc(db, "users", userId), {
          role: "seller",
          sellerStatus: "pending",
        });
      },

      approveSeller: async (userId) => {
        const { db } = requireDb();
        await updateDoc(doc(db, "users", userId), { sellerStatus: "approved" });
        await get().notify(userId, "Your seller application was approved. You can now list items.");
      },

      rejectSeller: async (userId) => {
        const { db } = requireDb();
        await updateDoc(doc(db, "users", userId), { sellerStatus: "rejected" });
        await get().notify(userId, "Your seller application was not approved this time.");
      },

      addProduct: async (product) => {
        const { db } = requireDb();
        const ref = doc(collection(db, "products"));
        const newProduct: Product = {
          ...product,
          id: ref.id,
          status: "pending",
          createdAt: Date.now(),
        };
        await setDoc(ref, newProduct);
        return newProduct;
      },

      approveProduct: async (productId) => {
        const { db } = requireDb();
        await updateDoc(doc(db, "products", productId), { status: "approved" });
        const p = get().products.find((p) => p.id === productId);
        if (p) await get().notify(p.sellerId, `"${p.title}" was approved and is now live.`);
      },

      rejectProduct: async (productId) => {
        const { db } = requireDb();
        await updateDoc(doc(db, "products", productId), { status: "rejected" });
        const p = get().products.find((p) => p.id === productId);
        if (p) await get().notify(p.sellerId, `"${p.title}" was rejected. Check listing guidelines.`);
      },

      toggleRareFind: async (productId) => {
        const { db } = requireDb();
        const p = get().products.find((p) => p.id === productId);
        if (!p) return;
        await updateDoc(doc(db, "products", productId), { isRareFind: !p.isRareFind });
      },

      updateProduct: async (productId, updates) => {
        const { db } = requireDb();
        await updateDoc(doc(db, "products", productId), updates);
      },

      deleteProduct: async (productId) => {
        const { db } = requireDb();
        await deleteDoc(doc(db, "products", productId));
      },

      addToCart: (productId, quantity = 1) =>
        set((s) => {
          const existing = s.cart.find((c) => c.productId === productId);
          if (existing) {
            return {
              cart: s.cart.map((c) =>
                c.productId === productId
                  ? { ...c, quantity: c.quantity + quantity }
                  : c
              ),
            };
          }
          return { cart: [...s.cart, { productId, quantity }] };
        }),

      removeFromCart: (productId) =>
        set((s) => ({ cart: s.cart.filter((c) => c.productId !== productId) })),

      clearCart: () => set({ cart: [] }),

      checkout: async () => {
        const { cart } = get();
        if (cart.length === 0) return false;

        const { url } = await callApi<{ url: string }>("/api/checkout/create-session", {
          method: "POST",
          body: JSON.stringify({
            items: cart.map((c) => ({ productId: c.productId, quantity: c.quantity })),
          }),
        });

        // Deliberately not clearing the cart here — if the buyer abandons
        // Stripe's hosted checkout and lands back on /cart via cancel_url,
        // their items should still be there. The success page clears it.
        window.location.href = url;
        return true;
      },

      startSellerPayoutsOnboarding: async () => {
        const { url } = await callApi<{ url: string }>("/api/connect/onboard", {
          method: "POST",
        });
        window.location.href = url;
      },

      refreshSellerPayoutsStatus: async () => {
        return callApi<{
          connected: boolean;
          chargesEnabled?: boolean;
          payoutsEnabled?: boolean;
          detailsSubmitted?: boolean;
        }>("/api/connect/status", { method: "GET" });
      },

      toggleFavourite: async (userId, productId) => {
        const { db } = requireDb();
        const current = get().favourites[userId] ?? [];
        const isFavourited = current.includes(productId);
        const ref = doc(db, "favourites", userId);
        await setDoc(
          ref,
          { productIds: isFavourited ? arrayRemove(productId) : arrayUnion(productId) },
          { merge: true }
        );
      },

      addReview: async (review) => {
        const { db } = requireDb();
        const ref = doc(collection(db, "reviews"));
        const newReview: Review = { ...review, id: ref.id, createdAt: Date.now() };
        await setDoc(ref, newReview);
      },

      notify: async (userId, message) => {
        const { db } = requireDb();
        const ref = doc(collection(db, "notifications"));
        const notification: Notification = {
          id: ref.id,
          userId,
          message,
          read: false,
          createdAt: Date.now(),
        };
        await setDoc(ref, notification);
      },

      markNotificationsRead: async (userId) => {
        const { db } = requireDb();
        const unread = get().notifications.filter((n) => n.userId === userId && !n.read);
        if (unread.length === 0) return;
        const batch = writeBatch(db);
        for (const n of unread) {
          batch.update(doc(db, "notifications", n.id), { read: true });
        }
        await batch.commit();
      },
    }),
    {
      name: "nerd-multiverse-cart",
      partialize: (s) => ({ cart: s.cart }),
    }
  )
);

// --- Live sync: Firestore/Auth -> store state ---------------------------
//
// Called once from App.tsx. Products and reviews are public and always
// synced; users/orders/notifications/favourites require a signed-in user
// (per firestore.rules) so those listeners are (re)attached on auth change.

let userScopedUnsubs: Unsubscribe[] = [];

function teardownUserScopedListeners() {
  userScopedUnsubs.forEach((unsub) => unsub());
  userScopedUnsubs = [];
}

export function initFirebaseSync() {
  if (!db || !auth) return () => {};

  const database = db;

  const unsubProducts = onSnapshot(collection(database, "products"), (snap) => {
    useAppStore.setState({ products: snap.docs.map((d) => d.data() as Product) });
  });

  const unsubReviews = onSnapshot(collection(database, "reviews"), (snap) => {
    useAppStore.setState({ reviews: snap.docs.map((d) => d.data() as Review) });
  });

  const unsubAuth = onAuthStateChanged(auth, async (firebaseUser) => {
    teardownUserScopedListeners();

    if (!firebaseUser) {
      useAppStore.setState({
        _currentUser: null,
        _authReady: true,
        users: [],
        orders: [],
        notifications: [],
        favourites: {},
      });
      return;
    }

    const profileSnap = await getDoc(doc(database, "users", firebaseUser.uid));
    useAppStore.setState({
      _currentUser: profileSnap.exists() ? (profileSnap.data() as AppUser) : null,
      _authReady: true,
    });

    userScopedUnsubs.push(
      onSnapshot(doc(database, "users", firebaseUser.uid), (snap) => {
        if (snap.exists()) {
          useAppStore.setState({ _currentUser: snap.data() as AppUser });
        }
      }),
      onSnapshot(collection(database, "users"), (snap) => {
        useAppStore.setState({ users: snap.docs.map((d) => d.data() as AppUser) });
      }),
      onSnapshot(collection(database, "orders"), (snap) => {
        useAppStore.setState({ orders: snap.docs.map((d) => d.data() as Order) });
      }),
      onSnapshot(
        query(collection(database, "notifications"), where("userId", "==", firebaseUser.uid)),
        (snap) => {
          useAppStore.setState({
            notifications: snap.docs
              .map((d) => d.data() as Notification)
              .sort((a, b) => b.createdAt - a.createdAt),
          });
        }
      ),
      onSnapshot(doc(database, "favourites", firebaseUser.uid), (snap) => {
        const productIds = (snap.data()?.productIds as string[] | undefined) ?? [];
        useAppStore.setState((s) => ({
          favourites: { ...s.favourites, [firebaseUser.uid]: productIds },
        }));
      })
    );
  });

  return () => {
    unsubProducts();
    unsubReviews();
    unsubAuth();
    teardownUserScopedListeners();
  };
}
